import { getToolName, isToolUIPart, type UIMessage } from "ai";

export function textFromMessage(message: UIMessage): string {
  return message.parts
    .filter((part): part is { type: "text"; text: string } => part.type === "text")
    .map((part) => part.text)
    .join("");
}

export function hasPendingToolInvocation(message: UIMessage | undefined): boolean {
  if (!message || message.role !== "assistant") return false;
  return message.parts.some((part) => {
    if (!isToolUIPart(part)) return false;
    return (
      part.state === "input-streaming" ||
      part.state === "input-available" ||
      part.state === "approval-requested" ||
      part.state === "approval-responded"
    );
  });
}

export function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** Resolved create_booking tool part with a bookingId in the output. */
export function parseResolvedCreateBooking(part: UIMessage["parts"][number]): {
  bookingId: string;
  location: string;
  date: string;
  time: string;
} | null {
  if (!isToolUIPart(part)) return null;
  if (getToolName(part) !== "create_booking") return null;
  const tp = part;
  if (tp.state !== "output-available") return null;
  const out = tp.output;
  if (!isRecord(out)) return null;
  const bookingId = out.bookingId;
  if (typeof bookingId !== "string" || !bookingId.trim()) return null;
  const details = isRecord(out.confirmationDetails) ? out.confirmationDetails : {};
  const inp = isRecord(tp.input) ? tp.input : {};
  const location = String(details.location ?? inp.location ?? "");
  const date = String(details.date ?? inp.date ?? "");
  const time = String(details.time ?? inp.time ?? "");
  return { bookingId, location, date, time };
}

export function formatConfirmationDateTime(dateStr: string, timeStr: string): string {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  const tm = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(timeStr);
  if (!dm || !tm) {
    const fallback = [dateStr, timeStr].filter((s) => s.trim()).join(" ");
    return fallback || "your scheduled time";
  }
  const y = Number(dm[1]);
  const mo = Number(dm[2]);
  const d = Number(dm[3]);
  const hh = Number(tm[1]);
  const mm = Number(tm[2]);
  const dt = new Date(y, mo - 1, d, hh, mm);
  const datePart = dt.toLocaleDateString("en-IE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  const hour24 = dt.getHours();
  const h12 = hour24 % 12 || 12;
  const suffix = hour24 >= 12 ? "pm" : "am";
  const timePart =
    mm === 0 ? `${h12}${suffix}` : `${h12}:${String(mm).padStart(2, "0")}${suffix}`;
  return `${datePart} at ${timePart}`;
}

export function findCreateBookingDetailsForId(
  messagesList: UIMessage[],
  bookingId: string
): { location: string; date: string; time: string } {
  for (const message of messagesList) {
    if (message.role !== "assistant") continue;
    for (const part of message.parts) {
      const parsed = parseResolvedCreateBooking(part);
      if (parsed?.bookingId === bookingId) {
        return {
          location: parsed.location,
          date: parsed.date,
          time: parsed.time,
        };
      }
    }
  }
  return { location: "", date: "", time: "" };
}
